import {
  Col,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from 'react-bootstrap'
import Navbar from '../../components/navbar/navbar'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { sendErrorApiMessage } from '../../utils/sendErrorApiMessage'
import apinode from '../../services/apinode'
import { ContentLoading } from '../../components/ContentLoading'
import { useOrdenation } from '../../hooks/useOrdenation'
import removeNonCharacters from '../../utils/removeNonCharacters'
import { CustomButton } from '../../components/CustomButton'
import {
  BoxArrowUpRight,
  ClipboardDataFill,
  FiletypePdf,
  Percent,
} from 'react-bootstrap-icons'
import { CustomInputDate } from '../../components/CustomInputDate'
import { unifyParams } from '../../utils/unifyParams'
import { formatPrice } from '../../utils/formatPrice'
import { formatDate } from '../../utils/formatDate'
import { formatPercent } from '../../utils/formatPercent'
import { Link } from 'react-router-dom'
import { gerarRelatorioComissoes } from './gerarRelatorioComissoes'
import { subMonths } from 'date-fns'
import { toast } from 'react-toastify'

export function ComissoesRepresentante() {
  const [representantes, setRepresentantes] = useState([]) // Geral
  const [representante, setRepresentante] = useState({
    info: {},
    comissoes: [],
  }) // Especifíco
  const [isRepresentantesLoading, setIsRepresentantesLoading] = useState(true)
  const [startDate, setStartDate] = useState(subMonths(new Date(), 1))
  const [endDate, setEndDate] = useState(new Date())
  const [nameSearch, setNameSearch] = useState('')
  const [documentSearch, setDocumentSearch] = useState('')
  const [isGetComissionLoading, setIsGetComissionLoading] = useState({})
  const [isModalComissoesOpen, setIsModalComissoesOpen] = useState(false)
  const [isGetProductsLoading, setIsGetProductsLoading] = useState({})
  const [isGeneratePDFLoading, setIsGeneratePDFLoading] = useState(false)

  const sortedItems = useMemo(() => {
    let data = representantes

    if (nameSearch) {
      data = data.filter((item) =>
        String(item.pes_razao).toLowerCase().includes(nameSearch.toLowerCase()),
      )
    }

    if (documentSearch) {
      data = data.filter((item) =>
        removeNonCharacters(item.pes_cnpj_cpf)
          .toLowerCase()
          .includes(documentSearch.toLowerCase()),
      )
    }

    return data
  }, [documentSearch, representantes, nameSearch])

  const { filteredItems, Th, Paginate } = useOrdenation(
    sortedItems,
    'pes_razao',
    'asc',
  )

  async function loadRepresentantes() {
    try {
      setIsRepresentantesLoading(true)

      const response = await apinode.get(
        `/comissoes-representante/representantes/${localStorage.idconta}`,
      )

      setRepresentantes(response.data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar representantes')
    } finally {
      setIsRepresentantesLoading(false)
    }
  }

  const handleGeneratePDF = useCallback(async () => {
    try {
      setIsGeneratePDFLoading(true)

      let data = {
        info: representante.info,
        comissoes: representante.comissoes,
      }

      for (const [index, comissao] of representante.comissoes.entries()) {
        const response = await apinode.get(
          `/comissoes-representante/produtos/${comissao.pedido}/${localStorage.idconta}`,
        )

        data = {
          ...data,
          comissoes: data.comissoes.with(index, {
            ...data.comissoes[index],
            products: response.data,
          }),
        }
      }

      await gerarRelatorioComissoes(data.info, data.comissoes, {
        startDate,
        endDate,
      })
    } catch (err) {
      console.log(err)
      toast.error('Erro ao gerar relatório')
    } finally {
      setIsGeneratePDFLoading(false)
    }
  }, [endDate, representante.comissoes, representante.info, startDate])

  async function handleGetComission(rep, index) {
    try {
      setIsGetComissionLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      const url = unifyParams(
        `/comissoes-representante/comissoes/${rep.cod_pessoa}/${localStorage.idconta}`,
        [
          {
            key: 'startDate',
            value: startDate,
          },
          {
            key: 'endDate',
            value: endDate,
          },
        ],
      )

      const response = await apinode.get(url)

      setRepresentante({
        info: rep,
        comissoes: response.data,
      })

      setIsModalComissoesOpen(true)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar a comissão')
    } finally {
      setIsGetComissionLoading((prevState) => ({
        ...prevState,
        [index]: false,
      }))
    }
  }

  async function handleGetProducts(pedido, index) {
    try {
      setIsGetProductsLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      const response = await apinode.get(
        `/comissoes-representante/produtos/${pedido}/${localStorage.idconta}`,
      )

      setRepresentante((prevState) => ({
        ...prevState,
        comissoes: prevState.comissoes.map((comissao, comissaoIndex) => {
          if (comissaoIndex == index) {
            return {
              ...comissao,
              products: response.data,
            }
          } else {
            return {
              ...comissao,
              products: [],
            }
          }
        }),
      }))
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar os produtos')
    } finally {
      setIsGetProductsLoading((prevState) => ({
        ...prevState,
        [index]: false,
      }))
    }
  }

  useEffect(() => {
    loadRepresentantes()
  }, [])

  return (
    <>
      <Navbar />

      <Container>
        <h1>Comissões Por Representante</h1>

        <Row>
          <Col>
            <Form.Label>Nome</Form.Label>
            <Form.Control
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              placeholder="Digite aqui..."
            />
          </Col>

          <Col>
            <Form.Label>CPF | CNPJ</Form.Label>
            <Form.Control
              value={documentSearch}
              onChange={(e) => setDocumentSearch(e.target.value)}
              placeholder="Digite aqui..."
            />
          </Col>

          <Col>
            <Form.Label>Comissão (Data Inicial)</Form.Label>
            <CustomInputDate
              selected={startDate}
              onChange={setStartDate}
            />
          </Col>

          <Col>
            <Form.Label>Comissão (Data Final)</Form.Label>
            <CustomInputDate
              selected={endDate}
              onChange={setEndDate}
            />
          </Col>
        </Row>

        <ContentLoading
          isLoading={isRepresentantesLoading}
          loadingText="Carregando representantes..."
        >
          {representantes.length === 0 ? (
            <p>Sem dados disponíveis</p>
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th
                      label="ID"
                      value="cod_pessoa"
                    />
                    <Th
                      label="Nome"
                      value="pes_razao"
                    />
                    <Th
                      label="CPF | CNPJ"
                      value="pes_cnpj_cpf"
                    />

                    <th>Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredItems.map((rep, index) => (
                    <tr>
                      <td>{rep.cod_pessoa}</td>
                      <td>{rep.pes_razao}</td>
                      <td>{rep.pes_cnpj_cpf}</td>
                      <td>
                        <OverlayTrigger
                          overlay={<Tooltip>Buscar Comissões</Tooltip>}
                        >
                          <CustomButton
                            onClick={() => handleGetComission(rep, index, true)}
                            isLoading={isGetComissionLoading[index]}
                          >
                            <Percent />
                          </CustomButton>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Paginate />
            </>
          )}
        </ContentLoading>
      </Container>

      <Modal
        show={isModalComissoesOpen}
        onHide={() => setIsModalComissoesOpen(false)}
        centered
        dialogClassName="custom-modalzpl"
      >
        <Modal.Header>
          <Modal.Title>Comissões</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <CustomButton
            title="Gerar Relatório"
            noMargin
            onClick={handleGeneratePDF}
            isLoading={isGeneratePDFLoading}
            isLoadingText="Gerando..."
          >
            <FiletypePdf />
          </CustomButton>

          {representante?.comissoes?.length === 0 ? (
            <p>Sem dados disponíveis</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Data</th>
                  <th>Total (R$)</th>
                  <th>Comissão (R$)</th>
                  <th>Ações</th>
                </tr>
              </thead>

              <tbody>
                {representante?.comissoes?.map((comissao, index) => (
                  <>
                    <tr>
                      <td>{comissao.pedido}</td>
                      <td>{comissao.cliente}</td>
                      <td>{formatDate(comissao.data)}</td>
                      <td>{formatPrice(comissao.total_pedido)}</td>
                      <td>{formatPrice(comissao.comissao)}</td>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            gap: 10,
                          }}
                        >
                          <OverlayTrigger
                            overlay={<Tooltip>Buscar Produtos</Tooltip>}
                          >
                            <CustomButton
                              noMargin
                              isLoading={isGetProductsLoading[index]}
                              onClick={() =>
                                comissao?.products?.length > 0
                                  ? setRepresentante((prevState) => ({
                                      ...prevState,
                                      comissoes: prevState.comissoes.with(
                                        index,
                                        {
                                          ...prevState.comissoes[index],
                                          products: [],
                                        },
                                      ),
                                    }))
                                  : handleGetProducts(comissao.pedido, index)
                              }
                            >
                              <ClipboardDataFill />
                            </CustomButton>
                          </OverlayTrigger>

                          <OverlayTrigger
                            overlay={<Tooltip>Ir até pedido</Tooltip>}
                          >
                            <Link
                              to={`/pedido/editar/${comissao.pedido}`}
                              target="_blank"
                            >
                              <CustomButton noMargin>
                                <BoxArrowUpRight />
                              </CustomButton>
                            </Link>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>

                    {comissao?.products?.length > 0 && (
                      <tr>
                        <td colSpan={6}>
                          <Table>
                            <thead>
                              <tr>
                                <th>SKU</th>
                                <th>Descrição</th>
                                <th>Qtd</th>
                                <th>Valor Unitário</th>
                                <th>Valor Total</th>
                                <th>Comissão (%)</th>
                                <th>Total Comissão</th>
                              </tr>
                            </thead>

                            <tbody>
                              {comissao.products.map((product) => (
                                <tr>
                                  <td>{product.codigo}</td>
                                  <td>{product.descricao}</td>
                                  <td>{product.quantidade_nova}</td>
                                  <td>{formatPrice(product.valorunitario)}</td>
                                  <td>
                                    {formatPrice(product.valor_total_item)}
                                  </td>
                                  <td>
                                    {formatPercent(product.porc_comissao)}
                                  </td>
                                  <td>{formatPrice(product.total_comissao)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}
